import { useEffect } from 'react';
import { extendTextSelectionFromAnchor } from '../../../utils/caretUtils';
import { deepBlockIdFor, rangeBetween } from '../../../utils/blockSelectionUtils';
import {
  DRAG_SELECT_PX,
  EDITOR_MENUS,
  HANDLE_OR_ZONE,
  blockIdAtHeight,
  blurFocusedField,
  clearNativeSelection,
  editableForBlock,
  isSelectableBlockTarget,
  resolveTextAnchor,
  sameIdRange,
} from '../utils/selectionDom';

/** Notion-style drag-across-blocks selection and shift+click ranges. */
export default function useDragBlockSelect({
  blocksRef,
  editorRootRef,
  rangeAnchorRef,
  multiSelectedRef,
  setSelectedBlockId,
  setMultiSelected,
}) {
  useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return undefined;

    let startDeepId = null;
    let startX = 0;
    let startY = 0;
    let dragging = false;
    // Character-offset anchor inside the start block's .doc-editable. Kept so
    // leaving into block multi-select and coming back can resume text highlight.
    let textAnchorOffset = null;
    // Once applyBlockRange blurs/clears the CE, the browser won't keep
    // extending a native text drag — we drive it ourselves when back inside.
    let nativeTextSelectKilled = false;
    let lastRangeKey = '';
    // Highlight is painted on the wrappers during the gesture. Committing it
    // through React on every new block re-renders every block already in the
    // range, which is the drag-across lag.
    let paintedIds = [];
    let blockEls = null;
    let chromeOn = false;
    let revealedFor = null;
    const revealed = new Set();
    let bands = null;
    const REVEAL_SEL =
      '.callout-content > .block-wrapper, .toggle-content .block-wrapper, .choice-path > .block-wrapper, .variables-panel__table-row';

    const revealAhead = (node) => {
      const block = node?.closest?.(REVEAL_SEL);
      if (!block) return;
      const mark = (el) => {
        if (!el || revealed.has(el)) return;
        revealed.add(el);
        el.style.contentVisibility = 'visible';
        bands = null;
      };
      // The block under the pointer, plus the next ones, so a skipped row is
      // laid out before the cursor reaches it. Otherwise the highlight waits
      // a frame for Chromium to un-skip it.
      mark(block);
      let sib = block.nextElementSibling;
      for (let i = 0; i < 8 && sib; i += 1) {
        mark(sib);
        sib = sib.nextElementSibling;
      }
    };

    const clearReveal = () => {
      revealed.forEach((el) => {
        el.style.contentVisibility = '';
      });
      revealed.clear();
      revealedFor = null;
      bands = null;
    };

    const idAtY = (y) => {
      if (!bands) {
        bands = [];
        const startEl = startDeepId
          ? root.querySelector(`[data-block-id="${startDeepId}"]`)
          : null;
        const parent = startEl?.parentElement;
        const nodes = parent
          ? parent.querySelectorAll(':scope > [data-block-id]')
          : root.querySelectorAll('.doc-editor-blocks > [data-block-id]');
        nodes.forEach((el) => {
          const r = el.getBoundingClientRect();
          bands.push({
            id: el.getAttribute('data-block-id'),
            top: r.top,
            bottom: r.bottom,
          });
        });
      }
      let found = null;
      for (const band of bands) {
        if (band.top > y) break;
        found = band.id;
        if (y <= band.bottom) return band.id;
      }
      return found;
    };

    const ensureChrome = () => {
      if (chromeOn) return;
      chromeOn = true;
      root.classList.add('doc-editor--selecting');
    };

    const clearChrome = () => {
      if (!chromeOn) return;
      chromeOn = false;
      root.classList.remove('doc-editor--selecting');
    };

    const elsById = () => {
      if (blockEls) return blockEls;
      blockEls = new Map();
      root.querySelectorAll('[data-block-id]').forEach((el) => {
        blockEls.set(el.getAttribute('data-block-id'), el);
      });
      return blockEls;
    };

    const paintMulti = (ids) => {
      const map = elsById();
      const prev = new Set(paintedIds);
      const next = new Set(ids);
      paintedIds.forEach((id) => {
        if (!next.has(id)) map.get(id)?.classList.remove('drag-lit');
      });
      ids.forEach((id) => {
        if (!prev.has(id)) map.get(id)?.classList.add('drag-lit');
      });
      paintedIds = ids;
    };

    const clearPaint = () => {
      if (!paintedIds.length) return;
      const map = blockEls;
      paintedIds.forEach((id) => {
        (map?.get(id) || root.querySelector(`[data-block-id="${id}"]`))?.classList.remove(
          'drag-lit'
        );
      });
      paintedIds = [];
      blockEls = null;
    };

    const commitPaint = () => {
      if (!paintedIds.length) {
        blockEls = null;
        return;
      }
      const ids = paintedIds;
      const els = ids.map((id) => blockEls.get(id));
      paintedIds = [];
      blockEls = null;
      setSelectedBlockId(null);
      setMultiSelected((prev) => (sameIdRange(prev, ids) ? prev : ids));
      // Drop the live class after React paints .multi-selected, so the
      // highlight doesn't blink off between the two.
      requestAnimationFrame(() => {
        els.forEach((el) => el?.classList.remove('drag-lit'));
      });
    };

    const resetDrag = () => {
      commitPaint();
      clearChrome();
      clearReveal();
      dragging = false;
      startDeepId = null;
      textAnchorOffset = null;
      nativeTextSelectKilled = false;
      lastRangeKey = '';
    };

    const applyBlockRange = (anchorId, endId) => {
      if (!anchorId || !endId || anchorId === endId) return false;
      const rangeKey = `${anchorId}\0${endId}`;
      if (rangeKey === lastRangeKey) return true;
      const range = rangeBetween(blocksRef.current, anchorId, endId);
      if (!range) return false;
      textAnchorOffset = resolveTextAnchor(textAnchorOffset, root, anchorId, startX, startY);
      lastRangeKey = rangeKey;
      if (dragging) {
        paintMulti(range);
        clearNativeSelection();
        if (!nativeTextSelectKilled) {
          nativeTextSelectKilled = true;
          const active = document.activeElement;
          if (active?.isContentEditable) active.blur();
        }
        return true;
      }
      clearNativeSelection();
      const active = document.activeElement;
      if (active?.isContentEditable) active.blur();
      nativeTextSelectKilled = true;
      setSelectedBlockId(null);
      setMultiSelected((prev) => (sameIdRange(prev, range) ? prev : range));
      return true;
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (e.target.closest(HANDLE_OR_ZONE) || e.target.closest(EDITOR_MENUS)) return;

      const clickedId = deepBlockIdFor(e.target);

      // Divider/image/video/snippet, and the chrome of a linked variable row,
      // select the block (no text caret). Fields inside the row still edit.
      if (isSelectableBlockTarget(e.target) && clickedId) {
        if (e.shiftKey) {
          const anchor =
            rangeAnchorRef.current || deepBlockIdFor(document.activeElement) || clickedId;
          if (applyBlockRange(anchor, clickedId)) {
            e.preventDefault();
            resetDrag();
            return;
          }
        }
        e.preventDefault();
        blurFocusedField();
        setMultiSelected((prev) => (prev.length ? [] : prev));
        setSelectedBlockId((prev) => (prev === clickedId ? null : clickedId));
        rangeAnchorRef.current = clickedId;
        resetDrag();
        return;
      }

      // Shift+click extends from the anchor — same range as dragging across.
      // Same-block shift+click is left to native text selection.
      if (e.shiftKey && clickedId) {
        const anchor =
          rangeAnchorRef.current || deepBlockIdFor(document.activeElement) || clickedId;
        if (applyBlockRange(anchor, clickedId)) {
          e.preventDefault();
          resetDrag();
          return;
        }
      }

      setMultiSelected((prev) => (prev.length ? [] : prev));
      setSelectedBlockId(null);
      startDeepId = clickedId;
      rangeAnchorRef.current = clickedId;
      startX = e.clientX;
      startY = e.clientY;
      dragging = true; // empty-space starts resolve their anchor on first move
      nativeTextSelectKilled = false;
      // Don't measure the caret here — caretRangeFromPoint lays out the page
      // before the highlight has started, which is what puts it behind the
      // pointer. The anchor is read once, when the drag actually leaves
      // this block.
      textAnchorOffset = null;
    };

    const onMouseMove = (e) => {
      if (!dragging || e.buttons !== 1) return;
      if (
        Math.abs(e.clientX - startX) < DRAG_SELECT_PX &&
        Math.abs(e.clientY - startY) < DRAG_SELECT_PX
      ) {
        return;
      }
      ensureChrome();
      // Still inside the block where the drag started: the browser is
      // painting the text highlight. Don't hit-test or restyle.
      const hit = e.target?.nodeType === 1 ? e.target : e.target?.parentElement;
      const fromHit = hit && deepBlockIdFor(hit);
      if (fromHit !== revealedFor) {
        revealedFor = fromHit;
        revealAhead(hit);
      }
      if (
        fromHit &&
        fromHit === startDeepId &&
        !paintedIds.length &&
        !nativeTextSelectKilled
      ) {
        return;
      }
      if (!startDeepId) startDeepId = blockIdAtHeight(root, startY);
      if (!startDeepId) return;
      rangeAnchorRef.current = startDeepId;
      // The event target is the element under the cursor. In the gap between
      // blocks, resolve from the row positions cached for this drag — a fresh
      // hit-test there is what left the highlight a beat behind the pointer.
      const overId =
        fromHit ||
        (hit && root.contains(hit)
          ? null
          : deepBlockIdFor(document.elementFromPoint(e.clientX, e.clientY))) ||
        idAtY(e.clientY) ||
        blockIdAtHeight(root, e.clientY);
      if (!overId || overId === startDeepId) {
        if (paintedIds.length) {
          clearPaint();
          lastRangeKey = '';
        }
        if (multiSelectedRef.current.length) {
          lastRangeKey = '';
          setMultiSelected([]);
        }
        if (overId === startDeepId && nativeTextSelectKilled && textAnchorOffset != null) {
          extendTextSelectionFromAnchor(
            editableForBlock(root, startDeepId),
            textAnchorOffset,
            e.clientX,
            e.clientY
          );
        }
        return;
      }
      applyBlockRange(startDeepId, overId);
    };

    const onMouseUp = () => {
      if (startDeepId) rangeAnchorRef.current = startDeepId;
      resetDrag();
    };

    root.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      root.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      clearPaint();
      clearChrome();
      clearReveal();
    };
  }, [blocksRef, editorRootRef]);
}
